import { useState } from "react";
import { Link } from "react-router-dom";
import { useVisualEditor } from "@/lib/useVisualEditor";
import { SectionRenderer } from "@/components/sections/SectionRenderer";
import { SectionFrame } from "@/components/editor/SectionFrame";
import { SectionEditDrawer } from "@/components/editor/SectionEditDrawer";
import { Loader } from "@/components/common/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus, Save, Send, Undo2, X, Monitor, Tablet, Smartphone, SlidersHorizontal,
} from "lucide-react";

const VIEWPORTS = { desktop: "100%", tablet: 834, phone: 414 };

// In-context page editor: the live page with per-section editing + right drawer.
export const VisualEditor = ({ pageKey, onExit }) => {
  const ed = useVisualEditor(pageKey);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [vw, setVw] = useState("desktop");

  const openEdit = (id) => { setSelectedId(id); setDrawerOpen(true); };
  const selected = ed.sections.find((s) => s.id === selectedId) || null;
  const pageName = ed.meta?.name || pageKey;

  if (ed.loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader /></div>;

  const vwBtn = (key, Icon) => (
    <button onClick={() => setVw(key)} data-testid={`vw-${key}`} title={key}
      className={`flex h-8 w-8 items-center justify-center rounded ${vw === key ? "bg-brand text-white" : "text-muted-foreground hover:bg-accent"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div data-testid="visual-editor">
      {/* Toolbar */}
      <div className="sticky top-0 z-[60] flex flex-wrap items-center justify-between gap-2 border-b border-border bg-background/95 px-3 py-2 shadow-sm backdrop-blur sm:px-4">
        <div className="flex items-center gap-2">
          <span className="font-condensed text-sm font-semibold uppercase tracking-wide">Editing: {pageName}</span>
          {ed.hasDraft && <span className="rounded bg-[hsl(48_96%_53%)] px-2 py-0.5 text-[10px] font-semibold uppercase text-black">Draft</span>}
          {ed.dirty && <span className="text-[11px] text-muted-foreground">unsaved…</span>}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <div className="mr-1 hidden items-center gap-0.5 rounded-md border border-border p-0.5 sm:flex">
            {vwBtn("phone", Smartphone)}{vwBtn("tablet", Tablet)}{vwBtn("desktop", Monitor)}
          </div>
          <Select onValueChange={(v) => { const id = ed.addSection(v); openEdit(id); }} value="">
            <SelectTrigger className="h-8 w-[112px] text-xs" data-testid="ve-add-section"><span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</span></SelectTrigger>
            <SelectContent className="z-[80]">
              <SelectItem value="rich_text">Text Block</SelectItem>
              <SelectItem value="image_text">Image + Text</SelectItem>
              <SelectItem value="cta_band">CTA Band</SelectItem>
              <SelectItem value="hero">Hero</SelectItem>
              <SelectItem value="gallery">Gallery</SelectItem>
              <SelectItem value="feature_grid">Feature Grid</SelectItem>
            </SelectContent>
          </Select>
          <Link to={`/admin/pages/${pageKey}`} title="Form editor" className="hidden h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-semibold uppercase hover:bg-accent transition-colors sm:inline-flex"><SlidersHorizontal className="h-3.5 w-3.5" /> Form</Link>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button disabled={!ed.hasDraft && !ed.dirty} data-testid="ve-discard" className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-semibold uppercase hover:bg-accent transition-colors disabled:opacity-50"><Undo2 className="h-3.5 w-3.5" /> Discard</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Discard draft changes?</AlertDialogTitle><AlertDialogDescription>This reverts the draft to the live published version.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={ed.discard} className="bg-destructive hover:bg-destructive/90">Discard</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button onClick={ed.saveDraft} disabled={ed.saving} data-testid="ve-save-draft" className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[hsl(var(--brand-red))] px-2.5 text-xs font-semibold uppercase text-brand hover:bg-brand/10 transition-colors disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save Draft</button>
          <button onClick={ed.commit} disabled={ed.saving} data-testid="ve-commit" className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand px-3 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Commit</button>
          <button onClick={onExit} data-testid="ve-exit" title="Exit edit mode" className="flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-accent transition-colors"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Page */}
      <div className={vw !== "desktop" ? "bg-muted py-4" : ""}>
        <div className="mx-auto bg-background transition-[width] duration-200" style={{ width: VIEWPORTS[vw], maxWidth: "100%" }}>
          {ed.sections.length === 0 ? (
            <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">No sections yet — use “Add” above.</div>
          ) : ed.sections.map((s, i) => (
            <SectionFrame
              key={s.id}
              section={s}
              index={i}
              total={ed.sections.length}
              selected={selectedId === s.id}
              onEdit={() => openEdit(s.id)}
              onUp={() => ed.move(s.id, -1)}
              onDown={() => ed.move(s.id, 1)}
              onToggleVisible={() => ed.toggleVisible(s.id)}
              onDuplicate={() => ed.duplicate(s.id)}
              onDelete={() => ed.remove(s.id)}
            >
              <SectionRenderer section={s} preview />
            </SectionFrame>
          ))}
        </div>
      </div>

      <SectionEditDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        section={selected}
        pageName={pageName}
        onPatch={(group, value) => ed.patch(selectedId, group, value)}
        onSave={ed.saveDraft}
        saving={ed.saving}
      />
    </div>
  );
};

export default VisualEditor;
